import React, { useRef, useEffect, useState, useMemo } from "react";
import { Feedback, FeedbackComment } from "../../ApiService";
import Button from "../Button/Button";
import Stars from "../Star/Stars";
import LoaderCircle from "../LoaderCircle/LoaderCircle";
import { isFeatureEnabled } from "../../config/features";
import { useMarketplace } from "../../contexts/MarketplaceContext";

export type FeedbackCardProps = {
  feedback: Feedback;
  replyValue: string;
  isLoading?: boolean;
  onSendReply: (id: string) => void;
  onReplyChange: (id: string, text: string) => void;
  onGenerateReply?: (id: string) => void;
};

const CommentChat: React.FC<{ comments: FeedbackComment[] }> = ({ comments }) => {
  if (!comments || comments.length === 0) return null;

  // Сортируем комментарии по дате (от старых к новым)
  const sortedComments = [...comments].sort((a, b) => {
    if (!a.createdDate || !b.createdDate) return 0;
    return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
  });

  return (
    <div className="mt-4 border-t pt-4">
      <div className="text-sm font-medium mb-2">Переписка с клиентом:</div>
      <div className="space-y-3">
        {sortedComments.map((comment, idx) => (
          <div
            key={idx}
            className={`p-3 rounded-lg max-w-[80%] ${comment.isFromSeller
              ? "bg-blue-50 ml-auto"
              : "bg-gray-50"
              }`}
          >
            <div className="text-xs text-gray-500 mb-1">
              {comment.isFromSeller ? "Вы" : "Клиент"}{" "}
              {comment.createdDate && `• ${new Date(comment.createdDate).toLocaleString()}`}
            </div>
            <div className="text-sm">{comment.text}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default React.memo(function FeedbackCard({
  feedback,
  replyValue,
  isLoading,
  onSendReply,
  onReplyChange,
  onGenerateReply,
}: FeedbackCardProps) {
  const { isWB } = useMarketplace();
  const isOzonWithComments = !isWB && !!feedback.comments?.length;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Локальное состояние для значения ответа
  const [localReplyValue, setLocalReplyValue] = useState(replyValue || '');

  // Обновляем локальное состояние при изменении пропса
  useEffect(() => {
    console.log(`FeedbackCard ${feedback.id} received new replyValue:`, {
      newValue: replyValue?.substring(0, 30) + (replyValue?.length > 30 ? '...' : '') || '',
      prevValue: localReplyValue.substring(0, 30) + (localReplyValue.length > 30 ? '...' : ''),
      isEqual: replyValue === localReplyValue,
      hasValue: !!replyValue
    });

    // Проверяем, что replyValue не undefined и не null
    if (replyValue !== undefined && replyValue !== null) {
      setLocalReplyValue(replyValue);

      // Убираем автофокус, чтобы избежать "перескакивания" курсора
      // при массовой генерации ответов
    }
  }, [replyValue, feedback.id]);

  // Обработчик локального изменения
  const handleLocalChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalReplyValue(newValue);
    onReplyChange(feedback.id, newValue);
  };

  // Собираем данные для отображения
  const displayData = useMemo(() => ({
    replyValue: localReplyValue,
    hasReplyValue: !!localReplyValue,
    replyValueLength: localReplyValue?.length || 0,
    canAnswer: feedback.canAnswer
  }), [localReplyValue, feedback.canAnswer]);

  // Проверяем localStorage, если replyValue пустой
  React.useEffect(() => {
    if (!localReplyValue && feedback.canAnswer !== false) {
      // Получаем ID текущего магазина из URL или другим способом
      const urlParams = new URLSearchParams(window.location.search);
      const storeId = urlParams.get('storeId');

      if (storeId) {
        const cachedRepliesData = localStorage.getItem(`feedback_replies_${storeId}`);
        if (cachedRepliesData) {
          try {
            const parsedReplies = JSON.parse(cachedRepliesData);
            if (parsedReplies[feedback.id]?.text) {
              console.log(`Found cached reply for ${feedback.id} in localStorage:`, parsedReplies[feedback.id].text.substring(0, 30));
              onReplyChange(feedback.id, parsedReplies[feedback.id].text);
            }
          } catch (e) {
            console.error('Error parsing cached replies:', e);
          }
        }
      }
    }
  }, [feedback.id, localReplyValue, feedback.canAnswer, onReplyChange]);

  React.useEffect(() => {
    // Логируем при изменении replyValue
    console.log(`Reply value changed for ${feedback.id}:`, {
      newValue: localReplyValue?.substring(0, 30) + (localReplyValue?.length > 30 ? '...' : ''),
      length: localReplyValue?.length || 0
    });
  }, [localReplyValue, feedback.id]);

  // Формируем ссылку на товар в зависимости от маркетплейса и наличия linkId
  const getProductUrl = () => {
    // Логируем для отладки
    console.log(`[FeedbackCard] Generating product URL for feedback ${feedback.id}:`, {
      isWB,
      marketplace: feedback.marketplace,
      linkId: feedback.linkId,
      sku: feedback.sku,
      wb_nmId: feedback.wb_nmId,
      ozon_sku: feedback.ozon_sku,
      productId: feedback.productId
    });

    // Проверяем наличие linkId и что это не undefined
    if (feedback.linkId !== undefined && feedback.linkId !== null) {
      if (isWB) {
        return `https://www.wildberries.ru/catalog/${feedback.linkId}/detail.aspx`;
      } else {
        return `https://www.ozon.ru/product/${feedback.linkId}`;
      }
    }

    // Для обратной совместимости используем sku
    if (feedback.sku !== undefined && feedback.sku !== null) {
      if (isWB) {
        return `https://www.wildberries.ru/catalog/${feedback.sku}/detail.aspx`;
      } else {
        return `https://www.ozon.ru/product/${feedback.sku}`;
      }
    }

    // Для WB можно также использовать wb_nmId
    if ((isWB || feedback.marketplace === 'wb') && feedback.wb_nmId) {
      return `https://www.wildberries.ru/catalog/${feedback.wb_nmId}/detail.aspx`;
    }

    // Для Ozon можно использовать ozon_sku
    if ((!isWB || feedback.marketplace === 'ozon') && feedback.ozon_sku) {
      return `https://www.ozon.ru/product/${feedback.ozon_sku}`;
    }

    // Если ничего не найдено, возвращаем undefined
    console.log(`[FeedbackCard] No product URL found for feedback ${feedback.id}`);
    return undefined;
  };

  const productUrl = getProductUrl();

  // Логируем для отладки
  console.log('FeedbackCard render:', displayData);

  return (
    <div className="mx-auto bg-white rounded-xl shadow-md overflow-hidden w-full my-4 relative">
      {isLoading && <LoaderCircle style={{ position: "absolute" }} />}
      <div className="flex flex-col p-8 gap-2 w-full ">
        <div className="flex justify-between items-center w-full gap-4">
          <div className="bodySemiboldFont">
            {/* Для WB используем productDetails.productName, для OZON - productName */}
            {(isWB ? feedback.productDetails?.productName : feedback.productName) && (
              <div className="text-base font-medium mb-1">
                {productUrl ? (
                  <a
                    href={productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    {isWB ? feedback.productDetails?.productName : feedback.productName}
                  </a>
                ) : (
                  <span>{isWB ? feedback.productDetails?.productName : feedback.productName}</span>
                )}
              </div>
            )}
            <div className="text-sm text-gray-600 flex flex-wrap gap-2">
              {feedback.productId && (
                <span>ID: {feedback.productId}</span>
              )}
              {(isWB ? feedback.wb_nmId : feedback.sku) && (
                <span>
                  {isWB ? 'nmID: ' : 'SKU: '}
                  {productUrl ? (
                    <a
                      href={productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {isWB ? feedback.wb_nmId : feedback.sku}
                    </a>
                  ) : (
                    isWB ? feedback.wb_nmId : feedback.sku
                  )}
                </span>
              )}
            </div>
          </div>
          <div className="bodyRegularFont text-gray-500 ">
            {(() => {
              try {
                const date = new Date(feedback.createdDate);
                if (isNaN(date.getTime())) {
                  console.warn(`Invalid date for feedback ${feedback.id}:`, feedback.createdDate);
                  return "Дата не указана";
                }
                return date.toLocaleDateString();
              } catch (error) {
                console.error(`Error formatting date for feedback ${feedback.id}:`, error);
                return "Дата не указана";
              }
            })()}
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2 items-center">
            <Stars count={feedback.productValuation || feedback.rating} />
            <span className="text-sm text-gray-500">
              {feedback.userName || feedback.author}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {feedback.text && (
              <p className="text-sm text-gray-800">{feedback.text}</p>
            )}
            {feedback.pros && (
              <div className="text-sm">
                <span className="font-medium text-green-600">Достоинства: </span>
                <span className="text-gray-800">{feedback.pros}</span>
              </div>
            )}
            {feedback.cons && (
              <div className="text-sm">
                <span className="font-medium text-red-600">Недостатки: </span>
                <span className="text-gray-800">{feedback.cons}</span>
              </div>
            )}
          </div>
        </div>

        {feedback.photos && feedback.photos.length > 0 && (
          <div className="flex flex-row flex-wrap gap-4 flex-shrink-0 my-2">
            {feedback.photos.map((photoUrl, idx) => (
              <img
                key={idx}
                className="h-[150px] object-cover w-[150px]"
                src={photoUrl}
                alt="Фото Клиента"
              />
            ))}
          </div>
        )}

        {/* Показываем чат для Озон отзывов */}
        {isFeatureEnabled('OZON_CHAT_UI_ENABLED') && isOzonWithComments && (
          <>
            <CommentChat comments={feedback.comments!} />
            {!feedback.canAnswer && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ На этот отзыв уже есть ответы. Дальнейшие ответы заблокированы.
                </p>
              </div>
            )}
          </>
        )}

        {/* Показываем поле ввода только если можно отвечать */}
        {feedback.canAnswer !== false && (
          <textarea
            ref={textareaRef}
            value={localReplyValue}
            data-feedback-id={feedback.id}
            className="w-full bodyRegularFont bg-gray-50 border border-gray-200 p-2 min-h-[80px] resize-none"
            onChange={handleLocalChange}
            placeholder={isOzonWithComments ? "Ваш ответ в переписке..." : "Ваш ответ на отзыв..."}
          />
        )}

        {feedback.canAnswer !== false && (
          <div className="flex justify-end items-center w-full mt-2 gap-2">
            {onGenerateReply && (
              <Button
                title="Сгенерировать"
                status={isLoading ? "disabled" : "enabled"}
                onClick={() => onGenerateReply(feedback.id)}
              />
            )}
            <Button
              title="Отправить"
              status={
                isLoading || !localReplyValue?.trim()
                  ? "disabled"
                  : "enabled"
              }
              onClick={() => onSendReply(feedback.id)}
            />
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Компонент будет перерендериваться только если изменились важные пропсы
  // Возвращаем true, если пропсы равны (не нужно перерендеривать)

  // Сравниваем только важные свойства
  const replyEqual = prevProps.replyValue === nextProps.replyValue;
  const loadingEqual = prevProps.isLoading === nextProps.isLoading;
  const idEqual = prevProps.feedback.id === nextProps.feedback.id;
  const canAnswerEqual = prevProps.feedback.canAnswer === nextProps.feedback.canAnswer;

  const propsEqual = replyEqual && loadingEqual && idEqual && canAnswerEqual;

  console.log(`Memo compare for ${prevProps.feedback.id}:`, {
    replyEqual,
    loadingEqual,
    idEqual,
    canAnswerEqual,
    result: propsEqual ? 'equal (no rerender)' : 'changed (will rerender)'
  });

  // Возвращаем true, если пропсы равны (избегаем ненужных рендеров)
  return propsEqual;
});
